import type { JSX } from 'react';
import { Children, cloneElement, forwardRef } from 'react';
import { styled } from 'styled-components';
import type { ButtonAsButtonProps, ButtonAsLinkProps, ButtonSizes } from '../Button/Button';
import { Button } from '../Button';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';

export type IconButtonProps = {
  /**
   * Accessible text for screen readers.
   */
  label: string;
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  children: JSX.Element;
} & (
  | Omit<ButtonAsButtonProps, 'fullWidth' | 'leftIcon' | 'rightIcon'>
  | Omit<ButtonAsLinkProps, 'fullWidth' | 'leftIcon' | 'rightIcon'>
);

const StyledButton = styled(Button)`
  --icon-button-size-sm: 24px;
  --icon-button-size-md: 32px;
  --icon-button-size-lg: 40px;
  --icon-button-size-xl: 48px;

  border-radius: 50%;
  height: ${({ size }) => `var(--icon-button-size-${size as ButtonSizes})`};
  width: ${({ size }) => `var(--icon-button-size-${size as ButtonSizes})`};
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
`;

/**
 * IconButton behaves like a [Button](?path=/docs/components-button--docs),
 * but only accepts an [Icon](?path=/docs/components-icon--docs) as a child.
 */
export const IconButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, IconButtonProps>(
  ({ children, label, size = 'md', ...props }: IconButtonProps, ref) => {
    const responsiveSize = useResponsiveProp(size);

    return (
      <StyledButton
        {...props}
        ref={ref}
        aria-label={label}
        data-wistia-ui-icon-button={true}
        size={responsiveSize}
      >
        {cloneElement(Children.only(children), {
          size: responsiveSize,
        })}
      </StyledButton>
    );
  },
);

IconButton.displayName = 'IconButton';
